import React, { useState, useMemo } from "react";
import { TRANSACTIONS, HISTORICAL_TOTAL, HISTORICAL_UNACCOUNTED, MONTHLY_INCOME } from "./data.js";

const TYPE_OPTIONS = [
  { id: "FIXED", label: "Fixed" },
  { id: "VARIABLE", label: "Variable" },
];

const CLASS_OPTIONS = [
  { id: "NEED", label: "Need" },
  { id: "WANT", label: "Want" },
];

export default function IntelStage({ onComplete }) {
  const [index, setIndex] = useState(0);
  const [tags, setTags] = useState({}); // { [id]: { type, class } }
  const [correctCount, setCorrectCount] = useState(0);
  const [flash, setFlash] = useState(null);

  const current = TRANSACTIONS[index];
  const done = index >= TRANSACTIONS.length;

  const tally = useMemo(() => {
    const entries = Object.values(tags);
    return { tagged: entries.length, total: TRANSACTIONS.length };
  }, [tags]);

  const chooseTag = (field, value) => {
    if (!current) return;
    const existing = tags[current.id] || {};
    const updated = { ...existing, [field]: value };
    setTags((prev) => ({ ...prev, [current.id]: updated }));

    if (updated.type && updated.class) {
      const bothCorrect = updated.type === current.correctType && updated.class === current.correctClass;
      if (bothCorrect) setCorrectCount((c) => c + 1);
      setFlash(bothCorrect ? "correct" : "off");
      setTimeout(() => {
        setFlash(null);
        setIndex((i) => i + 1);
      }, 550);
    }
  };

  if (done) {
    return (
      <div className="animate-popIn text-center">
        <p className="label-pixel mb-2 text-lime">Intel Complete</p>
        <h2 className="pixel-heading mb-4 text-base text-mist">Field Report Filed</h2>

        <div className="mx-auto mb-6 max-w-sm space-y-2 border-2 border-black bg-ink p-4 text-left text-sm">
          <div className="flex justify-between">
            <span className="text-mist/60">Tagged Correctly</span>
            <span className="text-lime">
              {correctCount}/{TRANSACTIONS.length}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-mist/60">Last Month, Income</span>
            <span className="text-gold">${MONTHLY_INCOME.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-mist/60">Last Month, Tracked Spending</span>
            <span className="text-cyan">${HISTORICAL_TOTAL.toLocaleString()}</span>
          </div>
          <div className="flex justify-between border-t border-black/50 pt-2">
            <span className="text-mist/60">Unaccounted For</span>
            <span className="text-danger">${HISTORICAL_UNACCOUNTED.toLocaleString()}</span>
          </div>
        </div>

        <p className="mx-auto mb-6 max-w-sm text-xs text-mist/60">
          Almost a third of last month's income never got a job to do — it just vanished into
          untracked spending. That's what a zero-based battle plan fixes: every soldier gets an
          order, so nothing goes missing.
        </p>

        <button onClick={() => onComplete({ correctCount })} className="btn-magenta">
          BUILD THE BATTLE PLAN ▶
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between font-display text-[9px] text-mist/60">
        <span>FIELD INTEL</span>
        <span>
          {tally.tagged}/{tally.total} REVIEWED
        </span>
      </div>

      <p className="mb-5 text-xs text-mist/60">
        Real transactions from last month's statements. Tag each one — this is the raw data every
        good budget is built from, not a guess.
      </p>

      <div
        key={current.id}
        className={`mb-5 border-2 border-black bg-ink p-5 transition-colors ${
          flash === "correct" ? "border-lime shadow-glow-cyan" : flash === "off" ? "border-danger" : ""
        }`}
      >
        <div className="mb-1 flex items-center justify-between text-xs text-mist/50">
          <span>{current.date}</span>
          <span className="text-gold">${current.amount}</span>
        </div>
        <p className="pixel-heading text-sm text-mist">{current.description}</p>
      </div>

      <div className="mb-4">
        <p className="label-pixel mb-2 text-mist/60">Spending Type</p>
        <div className="grid grid-cols-2 gap-2">
          {TYPE_OPTIONS.map((opt) => {
            const selected = tags[current.id]?.type === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => chooseTag("type", opt.id)}
                className={`border-2 px-3 py-2 font-display text-[9px] uppercase transition-colors ${
                  selected ? "border-cyan bg-cyan text-ink" : "border-mist/30 text-mist/70 hover:border-cyan"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="label-pixel mb-2 text-mist/60">Classification</p>
        <div className="grid grid-cols-2 gap-2">
          {CLASS_OPTIONS.map((opt) => {
            const selected = tags[current.id]?.class === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => chooseTag("class", opt.id)}
                className={`border-2 px-3 py-2 font-display text-[9px] uppercase transition-colors ${
                  selected ? "border-magenta bg-magenta text-ink" : "border-mist/30 text-mist/70 hover:border-magenta"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
